import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Play, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { Button } from "../components/ui/button";

const ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_82f7828c-c7a0-48a6-b1cb-fc4aadec5e5f/artifacts/4dy14776_Order%20%26%20Clean%20Co-%20Main%20Logo.jpeg",
  timelapse: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/r4m95dez_timelapse.mp4",
  // New professional before/after composite images
  beforeAfterGrid: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/1802ut2z_Order%20and%20Clean%20Co%202.png",
  singleDoubleComparison: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/wmqj8xm2_Order%20and%20Clean%20Co.png",
  // Legacy individual photos
  before1: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/pt40o4qr_20260131_115906.jpg",
  before2: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/v2psgkwa_20260131_120031.jpg",
  before3: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/s2koqmhx_20260131_122825.jpg",
  after1: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/4sqzggqv_20260131_153350.jpg",
  after2: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/6p0l0ygl_20260131_153419.jpg",
  // Flex Flooring images
  flexFlooring1: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/d9zikw23_Flex%20Flooring%201.jpeg",
  flexFlooring2: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/x6768j2k_Flex%20Fllooring%202.jpeg",
  flexFlooring3: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/i87nuj2p_Flex%20Flooring%203.jpeg",
  flexFlooring5: "https://customer-assets.emergentagent.com/job_garage-reset/artifacts/5ogjgwxd_Flex%20Flooring%205.jpeg"
};

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  const transformations = [
    { before: ASSETS.before1, after: ASSETS.after1, title: "Complete Garage Reset" },
    { before: ASSETS.before2, after: ASSETS.after2, title: "Declutter & Organise" },
    { before: ASSETS.before3, after: ASSETS.after1, title: "Space Transformation" },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % transformations.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + transformations.length) % transformations.length);
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-[#0A0A0A] overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, white 0, white 1px, transparent 0, transparent 50%)',
            backgroundSize: '20px 20px'
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <span className="caption text-slate-400 block mb-4">
                Sydney's Garage Cleanout Specialists
              </span>
              <h1 className="heading-xl text-white mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Transform Your<br />
                <span className="text-slate-300">Garage</span> in a<br />
                Single Day.
              </h1>
              <p className="body-lg text-slate-400 mb-8 max-w-lg">
                Professional garage cleanouts and organisation for Sydney homes. 
                Old-school quality, done right the first time.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/booking" data-testid="hero-book-btn">
                  <Button className="btn-primary w-full sm:w-auto">
                    Book Garage Service
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <Link to="/booking?assessment=true" data-testid="hero-assessment-btn">
                  <Button variant="outline" className="btn-secondary w-full sm:w-auto bg-transparent text-white border-white hover:bg-white hover:text-black">
                    Free Assessment
                  </Button>
                </Link>
              </div>
            </div>

            {/* Video/Image Section */}
            <div className="relative animate-fade-in">
              <div className="relative aspect-[4/3] rounded-sm overflow-hidden border-4 border-slate-800">
                {showVideo ? (
                  <video 
                    src={ASSETS.timelapse}
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <>
                    <img 
                      src={ASSETS.after1} 
                      alt="Transformed Garage" 
                      className="w-full h-full object-cover"
                    />
                    <button 
                      onClick={() => setShowVideo(true)}
                      className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-colors group"
                      data-testid="play-video-btn"
                    >
                      <div className="w-20 h-20 rounded-full bg-[#1a365d] flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play size={32} className="text-white ml-1" />
                      </div>
                    </button>
                  </>
                )}
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#1a365d] text-white px-6 py-3">
                <span className="font-bold text-2xl" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  WATCH THE TIMELAPSE
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="caption text-[#1a365d] block mb-4">How It Works</span>
            <h2 className="heading-lg text-[#0A0A0A]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              3 Simple Steps
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Book Assessment",
                description: "Complete our quick garage assessment form. We'll provide an instant quote based on your garage size and needs."
              },
              {
                step: "02",
                title: "We Clear & Reset",
                description: "Our professional team arrives on your scheduled date. We clear, sort, and organise your garage efficiently."
              },
              {
                step: "03",
                title: "Upgrade Your Space",
                description: "Optional enhancements like flooring, lighting, and shelving transform your garage into a premium space."
              }
            ].map((item, index) => (
              <div 
                key={index} 
                className="relative p-8 border-l-4 border-[#1a365d] bg-slate-50 group hover:bg-slate-100 transition-colors"
                data-testid={`step-${index + 1}`}
              >
                <span className="text-6xl font-bold text-slate-200 absolute top-4 right-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {item.step}
                </span>
                <h3 className="heading-md text-[#0A0A0A] mb-4 relative z-10" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-slate-600 relative z-10">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Gallery */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="caption text-slate-400 block mb-4">Transformations</span>
            <h2 className="heading-lg text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Before & After
            </h2>
          </div>

          {/* Professional Composite Images with Navigation */}
          <div className="relative">
            {currentSlide === 0 ? (
              <div className="relative">
                <img 
                  src={ASSETS.beforeAfterGrid}
                  alt="5 Garage Transformations - Before and After"
                  className="w-full object-contain"
                />
                <p className="text-center text-slate-400 mt-4">5 Complete Garage Transformations</p>
              </div>
            ) : (
              <div className="relative">
                <img 
                  src={ASSETS.singleDoubleComparison}
                  alt="Single and Double Garage Comparison"
                  className="w-full object-contain"
                />
                <p className="text-center text-slate-400 mt-4">Single & Double Garage Transformations</p>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-center items-center gap-4 mt-8">
              <button 
                onClick={() => setCurrentSlide(0)}
                className={`p-3 border-2 transition-colors ${currentSlide === 0 ? 'border-white bg-white/10' : 'border-slate-600 hover:border-white'}`}
                data-testid="gallery-prev"
              >
                <ChevronLeft size={24} className="text-white" />
              </button>
              <span className="text-white font-bold">
                {currentSlide + 1} / 2
              </span>
              <button 
                onClick={() => setCurrentSlide(1)}
                className={`p-3 border-2 transition-colors ${currentSlide === 1 ? 'border-white bg-white/10' : 'border-slate-600 hover:border-white'}`}
                data-testid="gallery-next"
              >
                <ChevronRight size={24} className="text-white" />
              </button>
            </div>

            {/* Dot indicators */}
            <div className="flex justify-center gap-2 mt-4">
              <button 
                onClick={() => setCurrentSlide(0)}
                className={`w-3 h-3 rounded-full transition-colors ${currentSlide === 0 ? 'bg-white' : 'bg-slate-600'}`}
              />
              <button 
                onClick={() => setCurrentSlide(1)}
                className={`w-3 h-3 rounded-full transition-colors ${currentSlide === 1 ? 'bg-white' : 'bg-slate-600'}`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="caption text-[#1a365d] block mb-4">Our Services</span>
            <h2 className="heading-lg text-[#0A0A0A]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Choose Your Service
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Garage Clear-Out */}
            <div className="card-service" data-testid="service-clearout">
              <span className="caption text-[#1a365d] block mb-2">Fast Removal</span>
              <h3 className="heading-md text-[#0A0A0A] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Garage Clear-Out
              </h3>
              <p className="text-slate-600 mb-6">
                Quick and efficient removal of clutter and unwanted items from your garage.
              </p>
              <ul className="space-y-2 mb-6">
                {["Loose item removal", "Council pickup separation", "Rubbish bagging", "Basic sweep & tidy"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-700">
                    <CheckCircle size={16} className="text-green-500" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex items-end justify-between border-t border-slate-200 pt-6">
                <div>
                  <span className="text-sm text-slate-500">Starting from</span>
                  <p className="text-3xl font-bold text-[#0A0A0A]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    $529
                  </p>
                </div>
                <Link to="/booking?service=clear_out">
                  <Button className="btn-primary" data-testid="book-clearout">
                    Book Now
                  </Button>
                </Link>
              </div>
            </div>

            {/* Garage Reset */}
            <div className="card-service border-[#1a365d] border-2" data-testid="service-reset">
              <span className="absolute top-0 right-0 bg-[#1a365d] text-white px-4 py-1 text-xs font-bold uppercase tracking-wider">
                Popular
              </span>
              <span className="caption text-[#1a365d] block mb-2">Full Transformation</span>
              <h3 className="heading-md text-[#0A0A0A] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Garage Reset
              </h3>
              <p className="text-slate-600 mb-6">
                Premium transformation with sorting, organisation, and structured layout.
              </p>
              <ul className="space-y-2 mb-6">
                {["Zone-by-zone clearing", "Keep/Donate/Dispose sorting", "Guided decision making", "Neat organisation", "Full clean & reset"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-700">
                    <CheckCircle size={16} className="text-green-500" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex items-end justify-between border-t border-slate-200 pt-6">
                <div>
                  <span className="text-sm text-slate-500">Starting from</span>
                  <p className="text-3xl font-bold text-[#0A0A0A]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    $849
                  </p>
                </div>
                <Link to="/booking?service=reset">
                  <Button className="btn-primary" data-testid="book-reset">
                    Book Now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Space Enhancements Preview */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="caption text-[#1a365d] block mb-4">After Your Service</span>
              <h2 className="heading-lg text-[#0A0A0A] mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Space Enhancements
              </h2>
              <p className="body-lg text-slate-600 mb-8">
                Transform your cleared garage into a premium space with our upgrade services. 
                From professional flooring to ceramic coating for your vehicles.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Flex Garage Flooring",
                  "Ceramic Coating",
                  "Vehicle Dashcam Installation",
                  "Epoxy Flooring",
                  "LED Lighting Installation"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <div className="w-2 h-2 bg-[#1a365d]" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/enhancements">
                <Button className="btn-secondary" data-testid="view-enhancements">
                  View All Upgrades
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src={ASSETS.flexFlooring3}
                alt="Professional garage flooring transformation"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-4 -left-4 bg-[#1a365d] text-white p-6">
                <p className="font-bold text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  UPGRADE YOUR SPACE
                </p>
                <p className="text-sm text-slate-300">Premium Services Available</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="caption text-[#1a365d] block mb-4">Testimonials</span>
            <h2 className="heading-lg text-[#0A0A0A]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              What Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Sarah M.",
                location: "Parramatta, Sydney",
                text: "Incredible transformation! They cleared years of accumulated stuff in just one day. My garage is finally usable again. Real tradies who know what they're doing.",
                rating: 5
              },
              {
                name: "James T.",
                location: "Castle Hill, Sydney",
                text: "The team was professional, efficient, and respectful of my belongings. Old-school work ethic - they got stuck in and didn't stop until it was perfect.",
                rating: 5
              },
              {
                name: "Michelle K.",
                location: "Sutherland Shire, Sydney",
                text: "I added the flooring upgrade after my cleanout and now my garage looks like a showroom. No shortcuts, just quality work. Highly recommend!",
                rating: 5
              }
            ].map((testimonial, index) => (
              <div 
                key={index} 
                className="p-8 bg-slate-50 border-l-4 border-[#1a365d]"
                data-testid={`testimonial-${index + 1}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-bold text-[#0A0A0A]">{testimonial.name}</p>
                  <p className="text-sm text-slate-500">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#1a365d]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="heading-lg text-white mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Ready to Transform Your Garage?
          </h2>
          <p className="body-lg text-slate-300 mb-8">
            Servicing Sydney & surrounding regions. Book your garage assessment today 
            and get an instant quote. Most jobs completed in a single day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking" data-testid="cta-book-btn">
              <Button className="bg-white text-[#1a365d] hover:bg-slate-100 px-8 py-4 uppercase font-bold tracking-wider text-sm transition-all">
                Book Now
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/services" data-testid="cta-services-btn">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1a365d] px-8 py-4 uppercase font-bold tracking-wider text-sm transition-all bg-transparent">
                View Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
